import {
  BillingAccountsTypeEnumType,
  Money,
  PaymentTermsTypeEnum,
  VatValidationStatusEnumType,
} from '__generated__/types';
import { Merge } from '@xometry/ui';
import { IAttachedFile } from 'interfaces/stores/AttachedFilesStore';

export enum BillingAccountType {
  Corporate = 'Corporate',
  Private = 'Private Person',
}

export interface BAPerson {
  id: number;
  name: string;
  billingAccountId?: string; // GraphQL parses ID as string
  organizationId?: string;
  organization?: {
    id: number;
    name: string;
  };
}

export interface BillingAccount {
  accountType: BillingAccountType;
  badPayer?: boolean;
  address?: string | null;
  city?: string | null;
  zip?: string | null;
  country?: string | null;
  creditLimitMoney?: Money;
  datevId?: string | null;
  intacctId?: string | null;
  intacctName?: string | null;
  dealsCount?: number;
  deletedAt?: string | null;
  einvoiceEnabled?: boolean;
  files?: IAttachedFile[];
  id?: string;
  internalComment?: string;
  mergedTo?: {
    id?: string;
    name?: string;
  };
  name?: string;
  nationalId?: string;
  paymentTermsUpdatedAt?: string;
  paymentTermsUpdatedBy?: {
    name: string;
  };
  paymentTermsUpdatedById?: string;
  paymentTermsUpdatedByName?: string;
  paymentTermsDays?: number;
  paymentTermsType?: PaymentTermsTypeEnum;
  peppolBuyerReference?: string | null;
  peppolIdType?: string | null;
  peppolParticipantId?: string | null;
  persons?: BAPerson[];
  region?: string | null;
  taxId?: string | null;
  type: BillingAccountsTypeEnumType;
  vatExempt?: boolean;
  vatExemptionCertificateNumber?: string | null;
  vatExemptionExpirationDate?: string | null;
  vatId?: string | null;
  notVatRegistered?: boolean;
  registeredAt?: string | null;
  vatRegistered?: boolean;
  vatRegistrationCountry?: string | null;
  vatValidationStatus?: VatValidationStatusEnumType | null;
  vatValidationTimestamp?: string;
}

type BillingAccountCachedBase = Pick<
  BillingAccount,
  | 'type'
  | 'accountType'
  | 'datevId'
  | 'intacctId'
  | 'city'
  | 'zip'
  | 'address'
  | 'country'
  | 'taxId'
  | 'vatId'
  | 'name'
  | 'internalComment'
  | 'vatValidationStatus'
  | 'vatValidationTimestamp'
  | 'deletedAt'
  | 'vatRegistrationCountry'
  | 'vatExempt'
  | 'vatRegistered'
>;

export interface BillingAccountCached extends BillingAccountCachedBase {
  id?: string;
  vatRegistered?: boolean;
}

interface BillingAccountCascadeObject {
  id: string;
  type: BillingAccountsTypeEnumType;
  accountType: string;
  address?: string | null;
  city?: string | null;
  zip?: string | null;
  country?: string | null;
  datevId?: string | null;
  intacctId?: string | null;
  internalComment?: string | null;
  name: string;
  taxId?: string | null;
  vatId?: string | null;
  vatValidationStatus?: VatValidationStatusEnumType | null;
}

export interface BaCascadeObject {
  deals?: Array<{
    id: number;
    pipedriveId?: number | null;
    person?: { id: number; name: string; pipedriveId?: number | null } | null;
    payments?: Array<{
      id: number;
      number?: string | null;
      billingAccount?: BillingAccountCascadeObject | null;
    }> | null;
    orderConfirmation?: {
      id: number;
      billingAccount?: BillingAccountCascadeObject | null;
    } | null;
    outcomingInvoices?: Array<{
      id: number;
      number?: number | null;
      creditNote: boolean;
      state?: string;
      billingAccount?: BillingAccountCascadeObject | null;
    }> | null;
  }> | null;
}

export type CascadeBillingAccount = Merge<
  NonNullable<NonNullable<NonNullable<BaCascadeObject['deals']>[number]['payments']>[number]['billingAccount']>,
  {
    intacctId?: string | null;
  }
>;
